/**
 * ErrorState — universal placeholder for failed-to-load states.
 *
 * Use as the visual building block for any "something went wrong"
 * surface: ErrorBoundary fallback, page-level fetch failure, inline
 * widget error. Mirrors the visual language of the assessment-
 * report `EmptyReport` (icon badge + title + message) but stripped
 * to one variant — `kind` is reserved for future expansion only.
 *
 * Default copy is sourced from i18n (`errors.unknownError` /
 * `btn.tryAgain`); the props let callers override per-context.
 */
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { styled } from '../styled';
import { Button } from '../Button';
import { AlertIcon, ReplayIcon } from '../icons';

const Wrap = styled('div', {
  shouldForwardProp: prop => prop !== '$fullHeight',
})(({ $fullHeight }) => ({
  margin: $fullHeight ? 0 : '48px auto',
  maxWidth: $fullHeight ? 'none' : 480,
  width: $fullHeight ? '100%' : 'auto',
  minHeight: $fullHeight ? '100%' : 'auto',
  padding: 40,
  textAlign: 'center',
  color: 'var(--qhs-text-2)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: $fullHeight ? 'center' : 'flex-start',
}));

const IconBadge = styled('div')({
  width: 60,
  height: 60,
  borderRadius: 12,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 16,
  background: 'var(--qhs-st-critical-bg)',
  color: 'var(--qhs-st-critical-tx)',
  '& svg': {
    width: 32,
    height: 32,
  },
});

const Title = styled('div')({
  fontFamily: 'var(--qhs-font-head)',
  fontSize: 18,
  fontWeight: 700,
  color: 'var(--qhs-text-1)',
  marginBottom: 6,
});

const Message = styled('div')({
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--qhs-text-2)',
  marginBottom: 20,
});

const ActionRow = styled('div')({
  display: 'flex',
  gap: 8,
  justifyContent: 'center',
});

const ErrorState = ({ title, message, onReload, reloadLabel, fullHeight, children }) => {
  const i18n = useSelector(state => state.i18n) || {};
  const errors = i18n.en?.errors || i18n.errors || {};
  const btn = i18n.en?.btn || i18n.btn || {};

  const resolvedTitle = title || errors.somethingWentWrongTitle || 'Something went wrong';
  const resolvedMessage =
    message ||
    errors.somethingWentWrongMessage ||
    'Please try again, or contact support if the issue persists.';
  const resolvedReloadLabel = reloadLabel || btn.retry || 'Retry';

  return (
    <Wrap $fullHeight={fullHeight}>
      <IconBadge>
        <AlertIcon />
      </IconBadge>
      <Title>{resolvedTitle}</Title>
      <Message>{resolvedMessage}</Message>
      {onReload && (
        <ActionRow>
          <Button variant="primary" onClick={onReload} startIcon={<ReplayIcon />}>
            {resolvedReloadLabel}
          </Button>
        </ActionRow>
      )}
      {children}
    </Wrap>
  );
};

ErrorState.propTypes = {
  title: PropTypes.node,
  message: PropTypes.node,
  onReload: PropTypes.func,
  reloadLabel: PropTypes.node,
  fullHeight: PropTypes.bool,
  children: PropTypes.node,
};

export default ErrorState;
